import { Injectable, OnDestroy } from '@angular/core';
import { ColDef, GridOptions } from 'ag-grid-community';
import {
  BehaviorSubject,
  combineLatest,
  map,
  merge,
  Observable,
  of,
  startWith,
  Subject,
  Subscription,
  switchMap,
  tap
} from 'rxjs';
import { RfqService } from '../../../services/rfq/rfq.service';
import { RfqTimelineEvent } from '../../../services/rfq/rfq.types';

@Injectable({
  providedIn: 'root'
})
export class RfqTimelineService implements OnDestroy {
  public agGridOptions$: Observable<GridOptions> = of(null);
  private agGridDefaultHeaders = new BehaviorSubject<ColDef[]>([]);
  private agGridDefaultHeaders$ = this.agGridDefaultHeaders.asObservable();
  private rfqTimelineEvents = new BehaviorSubject<RfqTimelineEvent[]>([]);
  private rfqTimelineEvents$ = this.rfqTimelineEvents.asObservable();

  private closeWindowSubject = new Subject<void>();
  private fetchDataSubject = new Subject<number>();
  private fetchDataSubscription: Subscription;

  public displayWindow$: Observable<boolean>;
  private dataAvailable$: Observable<boolean>;

  constructor(private rfqService: RfqService) {
    this.agGridOptions$ = combineLatest([
      this.agGridDefaultHeaders$,
      this.rfqTimelineEvents$
    ]).pipe(
      map(([headers, events]) => ({
        columnDefs: headers,
        rowData: events
      }))
    );

    this.fetchDataSubscription = this.fetchDataSubject
      .pipe(
        switchMap(requestId => {
          return this.rfqService.getRfqTimeline(requestId).pipe(
            tap((response: { events: RfqTimelineEvent[] }) => {
              this.rfqTimelineEvents.next(response.events);
            })
          );
        })
      )
      .subscribe();

    this.dataAvailable$ = this.agGridOptions$.pipe(
      map(gridOptions => {
        if (gridOptions.columnDefs.length && gridOptions.rowData.length)
          return true;
        return false;
      })
    );

    this.displayWindow$ = merge(
      this.dataAvailable$,
      this.closeWindowSubject.pipe(map(() => false))
    ).pipe(startWith(false));
  }

  public ngOnDestroy(): void {
    this.fetchDataSubscription?.unsubscribe();
  }

  public setAgGridRfqTimelineDefaultHeaders(payload): void {
    this.agGridDefaultHeaders.next(payload);
  }

  public setRfqTimelineResponse(events: RfqTimelineEvent[]): void {
    this.rfqTimelineEvents.next(events);
  }

  public closeWindow(): void {
    this.closeWindowSubject.next();
  }

  public fetchRfqTimeline(requestId: number): void {
    this.fetchDataSubject.next(requestId);
  }
}
